import React, { useMemo } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { CheckCircleFill } from 'react-bootstrap-icons';
import { CircleDashed, GripVertical, Sparkles } from 'lucide-react';
import { Button } from 'react-bootstrap';

const isHoaStepKey = key => /^hoa[1-3]$/.test(key || '');

const StepTab = ({
  step,
  isActive,
  formValues,
  onTabSelect,
  onToggleStepCompletion,
  tabSuffix,
  qaAnalysisRequired,
  qaUnresolvedStepKeys,
  dragHandleProps = null,
  isDragging = false
}) => {
  const sectionValues = formValues[step.key] || {};
  const stepIsComplete = !!sectionValues.isComplete;
  const hasUnresolvedQaIssues = qaUnresolvedStepKeys?.has(step.key);

  return (
    <div
      className={`px-3 py-2 hover-light cursor-pointer ${isActive ? 'border-dark' : ''}`}
      onClick={() => onTabSelect(step.key)}
      style={{
        boxSizing: 'border-box',
        borderBottom: isActive ? '3px solid' : 'none',
        opacity: isDragging ? 0.85 : 1
      }}
    >
      <div
        className={`d-flex align-items-center justify-content-center gap-2 px-2 ${
          stepIsComplete
            ? hasUnresolvedQaIssues && qaAnalysisRequired
              ? 'bg-danger'
              : 'bg-success'
            : 'bg-grey'
        }`}
        style={{ borderRadius: '8px' }}
      >
        {dragHandleProps ? (
          <span
            {...dragHandleProps}
            className="d-inline-flex align-items-center flex-shrink-0"
            style={{ cursor: 'grab' }}
            onClick={e => e.stopPropagation()}
            aria-label={`Drag to reorder ${step.name}`}
          >
            <GripVertical size={14} className={stepIsComplete ? 'text-white' : 'text-muted'} />
          </span>
        ) : null}
        <button
          type="button"
          className="p-0 mb-1 border-0 bg-transparent"
          onClick={e => {
            e.stopPropagation();
            onToggleStepCompletion(step.key, !stepIsComplete);
          }}
        >
          {stepIsComplete ? (
            <CheckCircleFill size={16} className="text-white" />
          ) : (
            <CircleDashed size={16} className="text-muted" fill="currentColor" />
          )}
        </button>
        <p
          className={`ml-1 my-0 py-1 font-weight-bold text-nowrap d-flex align-items-center ${
            stepIsComplete ? 'text-white' : 'text-muted'
          }`}
        >
          {step.name}
          {hasUnresolvedQaIssues && qaAnalysisRequired && !stepIsComplete ? (
            <span
              className="ml-1 d-inline-block rounded-circle flex-shrink-0"
              style={{ width: 8, height: 8, backgroundColor: '#FF0000' }}
              title="Unresolved QA issues"
              aria-label="Unresolved QA issues"
            />
          ) : null}
        </p>
        {typeof tabSuffix === 'function' ? tabSuffix(step, stepIsComplete) : null}
      </div>
    </div>
  );
};

const StepTabNavigation = ({
  formSteps,
  currentStep,
  formValues,
  onTabSelect,
  onToggleStepCompletion,
  tabSuffix,
  trailingContent,
  trailingAfterStepKey,
  trailingInlineContent,
  onHoaTabReorder = null,
  onQaAnalysisClick,
  qaAnalysisMeta,
  qaUnresolvedStepKeys = null
}) => {
  const qaAnalysisRequired = useMemo(() => onQaAnalysisClick && qaAnalysisMeta, [onQaAnalysisClick, qaAnalysisMeta]);
  const activeStepKey = formSteps[currentStep]?.key;
  const { prefixSteps, hoaSteps, suffixSteps } = useMemo(() => {
    const hoaIndices = formSteps.reduce((acc, step, index) => {
      if (isHoaStepKey(step.key)) acc.push(index);
      return acc;
    }, []);
    if (!hoaIndices.length) {
      return { prefixSteps: formSteps, hoaSteps: [], suffixSteps: [] };
    }
    const firstHoa = hoaIndices[0];
    const lastHoa = hoaIndices[hoaIndices.length - 1];
    return {
      prefixSteps: formSteps.slice(0, firstHoa),
      hoaSteps: formSteps.slice(firstHoa, lastHoa + 1),
      suffixSteps: formSteps.slice(lastHoa + 1)
    };
  }, [formSteps]);

  const hoaReorderEnabled = Boolean(onHoaTabReorder) && hoaSteps.length > 1;

  const renderStepTab = (step, { dragHandleProps = null, isDragging = false } = {}) => (
    <StepTab
      key={step.key}
      step={step}
      isActive={step.key === activeStepKey}
      formValues={formValues}
      onTabSelect={onTabSelect}
      onToggleStepCompletion={onToggleStepCompletion}
      tabSuffix={tabSuffix}
      qaAnalysisRequired={qaAnalysisRequired}
      qaUnresolvedStepKeys={qaUnresolvedStepKeys}
      dragHandleProps={dragHandleProps}
      isDragging={isDragging}
    />
  );

  const handleHoaDragEnd = result => {
    if (!result.destination || !onHoaTabReorder) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    if (fromIndex !== toIndex) onHoaTabReorder(fromIndex, toIndex);
  };

  const lastHoaStepKey = hoaSteps[hoaSteps.length - 1]?.key;

  return (
    <div className="d-flex justify-content-between w-100 pr-3">
      <div className="d-flex align-items-center justify-content-start gap-2 flex-wrap">
        {prefixSteps.map(step => renderStepTab(step))}
        {hoaReorderEnabled ? (
          <DragDropContext onDragEnd={handleHoaDragEnd}>
            <Droppable droppableId="hoa-report-tabs" direction="horizontal">
              {provided => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="d-flex align-items-center flex-wrap"
                  style={{ gap: 8 }}
                >
                  {hoaSteps.map((step, index) => (
                    <Draggable key={step.key} draggableId={step.key} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={dragProvided.draggableProps.style}
                        >
                          {renderStepTab(step, {
                            dragHandleProps: dragProvided.dragHandleProps,
                            isDragging: dragSnapshot.isDragging
                          })}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          hoaSteps.map(step => renderStepTab(step))
        )}
        {trailingAfterStepKey != null &&
        trailingInlineContent != null &&
        trailingAfterStepKey === lastHoaStepKey
          ? trailingInlineContent
          : null}
        {suffixSteps.map(step => renderStepTab(step))}
        {trailingContent != null ? trailingContent : null}
      </div>
      {!qaAnalysisMeta?.show && qaAnalysisRequired && (
        <div className="d-flex align-items-center justify-content-end">
          <Button
            variant="dark"
            size="md"
            className="d-inline-flex align-items-center font-weight-semibold text-nowrap"
            onClick={onQaAnalysisClick}
          >
            <Sparkles size={14} className="mr-1" aria-hidden />
            QA Analysis
          </Button>
        </div>
      )}
    </div>
  );
};

export default React.memo(StepTabNavigation);
